import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import CloudLocation from '../../models/cloudlocations';
import { ExternalContentProviders, ExternalContentProviderSource } from '../../constants/ExternalContentProviders.enum';
import { StoreDocumentsHyarchisDto } from '../../models/hyarchisStoreDocument';
import { Observable } from 'rxjs';
import { DossierChecklistInfo } from '../../models/hyarchisDossierChecklistInfo';
import CloudItem from 'src/app/models/clouditem';
import { ExportRequestModel } from '../external-cloudlocations.service';

export class ExportDocumentModel {
  requestId!: string;
  documentId!: string;
  drive!: string;
  folderId!: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExternalCloudLocationsApiClient {

  constructor(private http: HttpClient) { }

  configUrl = environment.apiUrl + "/api/external";
  httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };


  /** Url for the Hyarchis login popup (B2C or the organisation's own tenant), 400 'NotConfigured' when incomplete */
  getHyarchisAuthorizationUrl() {
    return this.http.get<{ url: string }>(this.configUrl + "/hyarchis/authorizationurl");
  }

  getContents(provider: string, source: string, purpose: string) {
    return this.http.get<CloudLocation[]>(this.configUrl + "/" + provider + "/" + source, { params: { 'purpose': purpose } });
  }
  /**
   * Get childs
   * @param searchTerm Optional, leave null or empty
   */
  getChildContents(provider: string, source: string, site: string, id: string, purpose: string, searchTerm: string) {
    if (searchTerm != null && searchTerm != '' && searchTerm != "") {
      return this.http.get<CloudLocation[]>(this.configUrl + "/" + provider + "/" + source + "/" + id, { params: { 'purpose': purpose, 'searchTerm': searchTerm } });
    }
    if (source === ExternalContentProviderSource.SharePoint) {
      if (site) return this.http.get<CloudLocation[]>(this.configUrl + "/" + provider + "/" + source + "/" + site + "/" + id);
      return this.http.get<CloudLocation[]>(this.configUrl + "/" + provider + "/" + source + "/drive/" + id);
    }
    return this.http.get<CloudLocation[]>(this.configUrl + "/" + provider + "/" + source + "/" + id);
  }

  getAncestors(provider: string, source: string, site: string, drive: string, itemId: string) {
    if (source === ExternalContentProviderSource.SharePoint && site) {
      return this.http.get<{ siteId?: string, teamId?: string, chain: any[] }>(this.configUrl + '/' + provider + '/' + source + '/ancestors/' + site + '/' + drive + '/' + itemId);
    }
    return this.http.get<{ siteId?: string, teamId?: string, chain: any[] }>(this.configUrl + '/' + provider + '/' + source + '/ancestors/' + drive + '/' + itemId);
  }

  search(provider: string, source: string, id: string, folderOnly: boolean, searchTerm: string, site?: string) {
    const body: any = {
      searchTerm: searchTerm,
      drive: id,
      foldersOnly: folderOnly ? 'true' : 'false'
    };
    if (site) body['site'] = site;
    return this.http.get<CloudLocation[]>(
      this.configUrl + "/" + provider + "/" + source + "/search",
      { params: body }
    );
  }

  /**
   * Get Partent child contents
   * @param searchTerm Optional, leave null or empty
   */
  getParentChildContents(provider: string, source: string, site: string, parent: string, id: string, purpose: string, searchTerm: string) {
    if (searchTerm != null && searchTerm != '' && searchTerm != "") {
      return this.http.get<CloudLocation[]>(this.configUrl + "/" + provider + "/" + source + "/" + parent + "/" + id, { params: { 'purpose': purpose, 'searchTerm': searchTerm } });
    }
    if (source === ExternalContentProviderSource.SharePoint) {
      if (site) return this.http.get<CloudLocation[]>(this.configUrl + "/" + provider + "/" + source + "/" + site + "/" + parent + "/" + id, { params: { 'purpose': purpose } });
      return this.http.get<CloudLocation[]>(this.configUrl + "/" + provider + "/" + source + "/drive/" + parent + "/" + id, { params: { 'purpose': purpose } });
    }
    return this.http.get<CloudLocation[]>(this.configUrl + "/" + provider + "/" + source + "/" + parent + "/" + id, { params: { 'purpose': purpose } });
  }

  async importChildContents(provider: string, parentid: string, id: string, allowRendering: boolean = true, workgroupId: string | null, clearancelevel: number | null, requestId: string | null, placeholderId?: number, workflowId?: string) {
    return this.http.post(this.configUrl + "/" + provider + "/import/" + parentid + "/" + id + "",
      {
        "WorkgroupId": workgroupId,
        "Clearancelevel": clearancelevel,
        "RequestId": requestId,
        "PlaceholderId": placeholderId,
        "WorkflowId": workflowId,
        "AllowRendering": allowRendering
      }, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      reportProgress: true,
      observe: "events"
    });
  }

  saveParentExternalContentForMsGraph(cloudItem: CloudItem, requestId: string, exportName: string) {
    const exportModel = new ExportRequestModel(
      requestId,
      exportName,
      cloudItem
    );
    return this.http.post(
      `${this.configUrl}/${cloudItem.provider}/store/${cloudItem.parentId}/${cloudItem.folderId}`, // 
      exportModel,
      this.httpOptions
    );
  }
  /**
   * call 'prepareStoreParentExternalContent' first and use modal for this (ChecklistItems must be chosen)
   * @param storeModel 
   * @param provider 
   * @param parentId 
   * @param id 
   * @returns 
   */
  saveParentExternalContentForHyarchis(storeModel: StoreDocumentsHyarchisDto, provider: ExternalContentProviders, parentId: string, id: string) {
    return this.http.post(this.configUrl + "/" + provider + "/store/" + parentId + "/" + id + "", storeModel, this.httpOptions);
  }

  prepareStoreParentExternalContent(cloudItem: CloudItem, dossierId: string, exportName: string): Observable<DossierChecklistInfo> {
    const exportModel = new ExportRequestModel(
      dossierId,
      exportName,
      cloudItem
    );
    return this.http.post<DossierChecklistInfo>(this.configUrl + "/" + cloudItem.provider + "/preparestore/" + cloudItem.parentId + "/" + cloudItem.folderId + "", exportModel, this.httpOptions);
  }

  saveDocumentToParentExternalContent(provider: string, parentId: string, id: string, requestId: string, documentId: string) {
    return this.http.post(this.configUrl + "/" + provider + "/storeDocument/" + parentId + "/" + id + "", { "requestId": "" + requestId + "", "documentId": "" + documentId + "" }, this.httpOptions);
  }

  addDocumentToCurrentRequest(requestId: string, documentIds: any) {
    return this.http.post(environment.apiUrl + "/requests/" + requestId + "/ImportDocuments", documentIds);
  }
}
